import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { isPrerendering } from '../prerenderData';
import './CheckoutPage.css';
import { apiFetch } from '../services/api';
import { trackPurchase } from '../services/googleAds';

// UX-17: map Stripe's `redirect_status` codes (and the absence of one)
// to a customer-facing reason. Internal status names like
// `requires_payment_method` or the literal word "unknown" should never
// reach the page.
function checkoutFailureReason(redirectStatus) {
  switch (redirectStatus) {
    case 'requires_payment_method':
      return 'Your payment didn\'t go through. The card was declined or the details couldn\'t be verified.';
    case 'requires_action':
      return 'Your payment needs an extra confirmation step from your bank that wasn\'t completed.';
    case 'processing':
      return 'Your payment was still processing when you came back. Please check your email for confirmation.';
    case 'canceled':
      return 'The payment was cancelled before it completed.';
    default:
      return 'Your payment didn\'t go through.';
  }
}

// Two ways the user can land on this page:
//  1. In-place confirmation (most cards + Apple/Google Pay): we navigate
//     here ourselves AFTER stripe.confirmPayment resolved cleanly. No
//     query params. The fact of being here = payment succeeded client-side.
//  2. Redirect flow (3DS / some APMs): Stripe brings us back with
//     ?payment_intent=...&redirect_status=succeeded|requires_payment_method|...
// In both cases the Order doc is the source of truth — webhook flips
// status to 'paid' a beat after the charge.
function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const redirectStatus = params.get('redirect_status') || '';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SEC-19: looked up by 128-bit random public_token, not by _id.
    // Keeps the previous "asg.lastOrderId" key as a one-release
    // fallback so an in-flight checkout from the previous bundle still
    // shows order details on the success page.
    const orderToken =
      sessionStorage.getItem('asg.lastOrderToken') ||
      sessionStorage.getItem('asg.lastOrderId');
    // Pre-render the loading state: it is what the client renders first.
    if (isPrerendering) return undefined;
    if (!orderToken) { setLoading(false); return; }
    let cancelled = false;
    const fetchOrder = () => apiFetch(`/api/checkout/order/${orderToken}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => { if (!cancelled) setOrder(o); return o; });
    // Fetch immediately, then every 2s (up to five more times) until the
    // webhook has flipped the status to paid. It usually lands in under a
    // second but can be slower under load, and the purchase conversion below
    // waits for it.
    let timer = null;
    const poll = (triesLeft) => fetchOrder()
      .then((o) => {
        if (cancelled) return;
        if (o && o.status !== 'paid' && triesLeft > 0) {
          if (triesLeft === 5) setLoading(false);
          timer = setTimeout(() => poll(triesLeft - 1), 2000);
        } else {
          setLoading(false);
        }
      })
      .catch(() => !cancelled && setLoading(false));
    poll(5);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  // Google Ads purchase conversion - only once the server says the order is
  // paid. trackPurchase ignores anything else and never sends twice for the
  // same order in a session.
  useEffect(() => { trackPurchase(order); }, [order]);

  // Show success if either the Order doc exists (we got here cleanly
  // via stripe.confirmPayment) OR Stripe explicitly said succeeded in
  // the redirect query string. Only show the failure UI when Stripe
  // told us the redirect failed AND we have no order to corroborate.
  const succeeded = !!order || redirectStatus === 'succeeded';

  return (
    <div className="co-page">
      <SEO title="Order confirmation" description="Thank you for your ASG Shading order. Your order confirmation and the next steps for delivery are on this page." canonical="/checkout/success" noindex />
      <div className="co-inner">
        <div className="co-left" style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          {loading ? (
            <h1 className="co-title">Confirming your order…</h1>
          ) : succeeded ? (
            <>
              <div className="co-success-tick">✓</div>
              <h1 className="co-title">Thank you{order?.customer_name ? `, ${order.customer_name.split(' ')[0]}` : ''}.</h1>
              <p className="co-success-sub">
                Your payment was successful and your order is confirmed. A receipt and order details
                {order?.customer_email ? <> have been emailed to <strong>{order.customer_email}</strong></> : ' have been emailed to you'}.
              </p>
              {order && (
                <div className="co-success-detail">
                  <div><strong>{order.product_name}</strong>{order.variant_label ? ` - ${order.variant_label}` : ''}</div>
                  {order.colour_name && <div className="co-summary-line--meta">Colour: {order.colour_name}</div>}
                  {(order.addons || []).map((a) => (
                    <div key={a.name} className="co-summary-line--meta">
                      + {a.quantity > 1 ? `${a.quantity} × ` : ''}{a.name}{a.included ? ' (included free)' : ''}
                    </div>
                  ))}
                  <div className="co-success-total">Total paid: ${(order.amount_cents / 100).toLocaleString()} {String(order.currency || 'aud').toUpperCase()}</div>
                </div>
              )}
              <p className="co-success-next">
                We'll be in touch within 1 to 2 business days to confirm your order and delivery details.
              </p>
              <Link to="/" className="co-pay-btn" style={{ display: 'inline-block', textDecoration: 'none' }}>Back to home</Link>
            </>
          ) : (
            <>
              <h1 className="co-title">Payment not completed</h1>
              {/* UX-17: was showing the raw Stripe `redirect_status`
                  value (or the literal word "unknown" when the param
                  was missing) inside parentheses. Customers don't
                  recognise those strings. Now mapped to plain English. */}
              <p>{checkoutFailureReason(redirectStatus)} No charge was made.</p>
              <Link to="/" className="co-pay-btn" style={{ display: 'inline-block', textDecoration: 'none' }}>Back to home</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CheckoutSuccessPage;
